import useQuery from '@/api/queries/useQuery';
import { CubeQuery } from '@/types/cube';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import {
  getValueFromTimeComparisonResult,
  useGetAdditionalFilter,
  useGetDateRangeTimeDimensionComparison,
  useGetGlobalFilters,
} from '@/utils/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { getCountChange } from '@/utils/timeComparison';
import { useGetHcpTotalCount } from '../hcp';

import { CUBE_AGGREGATE_INTERACTION } from '@/api/hcp/interaction/types';
import { useInteractionChannelFilters } from '@/context/ChanelFocus/AdditionalFiltersContext';
import { AdditionalChannelTabs } from '@/models';
import { EngagementViewsKeys } from './cacheKeys';

export interface OverallCoverage {
  hcpsContacted: {
    currentValue: number;
    // Null when the previous period had no contacted HCPs, so no change can be derived.
    changePercentage: number | null;
    // Whole percent, unlike the ratios consumers work with.
    previousCoverage?: number;
  };
  overallCoverage: {
    currentValue: number;
    changePercentage: number;
  };
}

type UseGetUniqueCustomersReachedResponse = OverallCoverage;

/**
 * @description Number of unique HCPs who have been contacted in current
 * filters selection and coverage regarding total hcp count
 * @returns OverallCoverage
 */
export function useGetUniqueCustomersReached(): UseQueryResult<UseGetUniqueCustomersReachedResponse, AxiosError> {
  const dateRange = useGetDateRangeTimeDimensionComparison(`${CUBE_AGGREGATE_INTERACTION}.interaction_date`);
  const globalFilters = useGetGlobalFilters();
  const { data: totalHcps, isLoading: isLoadingTotalHcps } = useGetHcpTotalCount();
  const { getFiltersForChannel } = useInteractionChannelFilters();
  const allChannelFilters = getFiltersForChannel(AdditionalChannelTabs.ALL_CHANNEL);
  const additionalFilter = useGetAdditionalFilter(allChannelFilters, AdditionalChannelTabs.ALL_CHANNEL);
  const query: CubeQuery = {
    measures: [`${CUBE_AGGREGATE_INTERACTION}.countHcpWithAtLeastOneInteraction`],
    limit: 1,
    timeDimensions: [dateRange],
    filters: [
      {
        member: `${CUBE_AGGREGATE_INTERACTION}.hcp_bk`,
        operator: 'set',
      },
      globalFilters,
      additionalFilter,
    ],
  };

  const results = useQuery<CubeRawRow[], AxiosError, UseGetUniqueCustomersReachedResponse>({
    payload: query,
    options: {
      queryKey: !isLoadingTotalHcps ? [...EngagementViewsKeys.uniqueCustomersReached(query)] : undefined,
      select: (data): UseGetUniqueCustomersReachedResponse => {
        if (!data.length)
          return {
            hcpsContacted: { currentValue: 0, changePercentage: 0 },
            overallCoverage: { currentValue: 0, changePercentage: 0 },
          };
        const [res] = data;
        const currentAndPrevData = getValueFromTimeComparisonResult(
          res,
          dateRange,
          `${CUBE_AGGREGATE_INTERACTION}.countHcpWithAtLeastOneInteraction`,
        );

        const currentCoverage = (currentAndPrevData.current / totalHcps) * 100;
        const previousCoverage = Math.round((currentAndPrevData.previous / totalHcps) * 100);
        const coverageChange = (previousCoverage - currentCoverage) / currentCoverage;

        return {
          hcpsContacted: {
            currentValue: currentAndPrevData.current,
            changePercentage: getCountChange(currentAndPrevData),
            previousCoverage,
          },
          overallCoverage: {
            currentValue: currentCoverage,
            changePercentage: coverageChange,
          },
        };
      },
      enabled: !isLoadingTotalHcps,
    },
  });

  return useMemo(
    () => ({
      ...results,
      isLoading: isLoadingTotalHcps || results.isLoading,
    }),
    [results, isLoadingTotalHcps],
  ) as UseQueryResult<UseGetUniqueCustomersReachedResponse, AxiosError>;
}
